import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getStockDetail, getChartData, getOrderbook, getStockNews, getDisclosures, formatNumber, formatPrice, formatChange } from '../../services/stockApi';
import { StockChart, OrderBook, NewsPanel, DisclosuresPanel } from './components';

const StockDetail = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stock, setStock] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [orderbook, setOrderbook] = useState(null);
  const [newsData, setNewsData] = useState(null);
  const [disclosuresData, setDisclosuresData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(false);
  const [disclosuresLoading, setDisclosuresLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('chart');
  const [isWatched, setIsWatched] = useState(false);
  const [chartPeriod, setChartPeriod] = useState('3mo');
  const [showMA5, setShowMA5] = useState(true);
  const [showMA20, setShowMA20] = useState(true);
  const [showMA60, setShowMA60] = useState(false);

  // 종목 상세 정보 로드
  useEffect(() => {
    const fetchStockDetail = async () => {
      try {
        setLoading(true);
        const response = await getStockDetail(symbol);
        if (response.success) {
          setStock(response.data);
        }
      } catch (error) {
        console.error('종목 상세 조회 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStockDetail();
  }, [symbol]);

  // 차트 데이터 로드
  const fetchChartData = useCallback(async () => {
    try {
      setChartLoading(true);
      const response = await getChartData(symbol, chartPeriod);
      if (response.success) {
        setChartData(response.data);
      }
    } catch (error) {
      console.error('차트 데이터 조회 실패:', error);
    } finally {
      setChartLoading(false);
    }
  }, [symbol, chartPeriod]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  // 호가 데이터 로드
  const fetchOrderbook = useCallback(async () => {
    try {
      const response = await getOrderbook(symbol);
      if (response.success) {
        setOrderbook(response.data);
      }
    } catch (error) {
      console.error('호가 데이터 조회 실패:', error);
    }
  }, [symbol]);

  useEffect(() => {
    if (activeTab === 'orderbook') {
      fetchOrderbook();
    }
  }, [activeTab, fetchOrderbook]);

  // 뉴스 데이터 로드
  const fetchNews = useCallback(async () => {
    try {
      setNewsLoading(true);
      const response = await getStockNews(symbol, 20);
      if (response.success) {
        // 뉴스 데이터 형식 변환
        const news = response.data || [];
        const positiveCount = news.filter(n => n.sentiment === 'positive').length;
        const negativeCount = news.filter(n => n.sentiment === 'negative').length;
        const neutralCount = news.filter(n => n.sentiment === 'neutral').length;

        // 감성 점수 계산 (-1 ~ 1)
        const total = news.length || 1;
        const sentimentScore = (positiveCount - negativeCount) / total;

        setNewsData({
          news,
          analysis: {
            total: news.length,
            positive_count: positiveCount,
            negative_count: negativeCount,
            neutral_count: neutralCount,
            sentiment_score: sentimentScore,
          }
        });
      }
    } catch (error) {
      console.error('뉴스 조회 실패:', error);
    } finally {
      setNewsLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    if (activeTab === 'news' && !newsData) {
      fetchNews();
    }
  }, [activeTab, newsData, fetchNews]);

  // 공시 데이터 로드
  const fetchDisclosures = useCallback(async () => {
    try {
      setDisclosuresLoading(true);
      const response = await getDisclosures(symbol, 20);
      if (response.success) {
        setDisclosuresData(response.data);
      }
    } catch (error) {
      console.error('공시 정보 조회 실패:', error);
    } finally {
      setDisclosuresLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    if (activeTab === 'disclosures' && !disclosuresData) {
      fetchDisclosures();
    }
  }, [activeTab, disclosuresData, fetchDisclosures]);

  const handleWatchToggle = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setIsWatched(!isWatched);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-gray-400">
        <span className="w-12 h-12 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></span>
        <span>종목 정보 불러오는 중...</span>
      </div>
    );
  }

  if (!stock) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-gray-400">
        <span className="text-[56px] opacity-60">❌</span>
        <p>종목을 찾을 수 없습니다.</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-6 py-3 bg-blue-500 border-none rounded-lg text-white text-[15px] font-medium cursor-pointer transition-colors duration-150 hover:bg-blue-600"
        >
          홈으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans pb-[100px]">
      {/* 상단 종목 정보 */}
      <header className="px-5 py-4 pb-6 bg-white border-b border-gray-200 md:px-4 md:py-3 md:pb-5">
        <button
          className="inline-flex items-center gap-1 py-2 bg-transparent border-none text-gray-400 text-[13px] cursor-pointer mb-4 transition-colors duration-150 hover:text-gray-900"
          onClick={() => navigate(-1)}
        >
          ← 뒤로
        </button>

        <div className="flex justify-between items-start mb-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold text-gray-900 m-0 tracking-tight md:text-xl">{stock.name}</h1>
            <span className="text-[13px] text-gray-400">{stock.symbol} · {stock.market}</span>
          </div>
          <button
            className={`w-11 h-11 bg-gray-50 border border-gray-200 rounded-full text-xl cursor-pointer transition-all duration-150 flex items-center justify-center hover:bg-gray-100 ${isWatched ? 'bg-amber-50 border-amber-400 text-amber-500' : ''}`}
            onClick={handleWatchToggle}
          >
            {isWatched ? '★' : '☆'}
          </button>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-4xl font-bold text-gray-900 tracking-tight md:text-3xl">{formatPrice(stock.price)}</span>
          <div className="flex items-center gap-2">
            <span className={`text-lg font-semibold md:text-base ${stock.change > 0 ? 'text-red-500' : stock.change < 0 ? 'text-blue-500' : ''}`}>
              {stock.change > 0 ? '+' : ''}{formatNumber(stock.changeAmount)}원
            </span>
            <span className={`text-lg font-semibold md:text-base ${stock.change > 0 ? 'text-red-500' : stock.change < 0 ? 'text-blue-500' : ''}`}>
              ({formatChange(stock.change)})
            </span>
          </div>
        </div>
      </header>

      {/* 탭 네비게이션 */}
      <nav className="flex bg-white border-b border-gray-200 overflow-x-auto scrollbar-hide">
        <button
          className={`flex-1 px-5 py-4 bg-transparent border-none border-b-2 border-transparent text-gray-400 text-[15px] font-medium cursor-pointer transition-all duration-150 whitespace-nowrap hover:text-gray-500 ${activeTab === 'chart' ? 'text-gray-900 border-b-gray-900 font-semibold' : ''}`}
          onClick={() => setActiveTab('chart')}
        >
          차트
        </button>
        <button
          className={`flex-1 px-5 py-4 bg-transparent border-none border-b-2 border-transparent text-gray-400 text-[15px] font-medium cursor-pointer transition-all duration-150 whitespace-nowrap hover:text-gray-500 ${activeTab === 'orderbook' ? 'text-gray-900 border-b-gray-900 font-semibold' : ''}`}
          onClick={() => setActiveTab('orderbook')}
        >
          호가
        </button>
        <button
          className={`flex-1 px-5 py-4 bg-transparent border-none border-b-2 border-transparent text-gray-400 text-[15px] font-medium cursor-pointer transition-all duration-150 whitespace-nowrap hover:text-gray-500 ${activeTab === 'info' ? 'text-gray-900 border-b-gray-900 font-semibold' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          종목정보
        </button>
        <button
          className={`flex-1 px-5 py-4 bg-transparent border-none border-b-2 border-transparent text-gray-400 text-[15px] font-medium cursor-pointer transition-all duration-150 whitespace-nowrap hover:text-gray-500 ${activeTab === 'disclosures' ? 'text-gray-900 border-b-gray-900 font-semibold' : ''}`}
          onClick={() => setActiveTab('disclosures')}
        >
          공시
        </button>
        <button
          className={`flex-1 px-5 py-4 bg-transparent border-none border-b-2 border-transparent text-gray-400 text-[15px] font-medium cursor-pointer transition-all duration-150 whitespace-nowrap hover:text-gray-500 ${activeTab === 'news' ? 'text-gray-900 border-b-gray-900 font-semibold' : ''}`}
          onClick={() => setActiveTab('news')}
        >
          뉴스
        </button>
      </nav>

      {/* 탭 콘텐츠 */}
      <div className="px-5 py-6 max-w-[800px] mx-auto md:p-4">
        {/* 차트 탭 */}
        {activeTab === 'chart' && (
          <div className="flex flex-col gap-6">
            {/* 기간 선택 */}
            <div className="flex gap-2">
              {['1m', '3mo', '6mo', '1y', '2y'].map((period) => (
                <button
                  key={period}
                  className={`px-4 py-2 rounded-lg text-[13px] font-medium cursor-pointer transition-all duration-150 ${chartPeriod === period ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  onClick={() => setChartPeriod(period)}
                >
                  {period === '1m' ? '1개월' :
                   period === '3mo' ? '3개월' :
                   period === '6mo' ? '6개월' :
                   period === '1y' ? '1년' : '2년'}
                </button>
              ))}
            </div>

            {/* 이동평균선 토글 */}
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showMA5}
                  onChange={(e) => setShowMA5(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-[13px] font-medium text-orange-500">MA5</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showMA20}
                  onChange={(e) => setShowMA20(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-[13px] font-medium text-purple-500">MA20</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showMA60}
                  onChange={(e) => setShowMA60(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-[13px] font-medium text-green-500">MA60</span>
              </label>
            </div>

            {/* 차트 */}
            {chartLoading ? (
              <div className="flex flex-col items-center justify-center h-80 bg-white border border-gray-200 rounded-2xl text-gray-400">
                <span className="w-12 h-12 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4"></span>
                <span>차트 데이터 로딩 중...</span>
              </div>
            ) : chartData && chartData.candles ? (
              <StockChart
                candles={chartData.candles}
                showMA5={showMA5}
                showMA20={showMA20}
                showMA60={showMA60}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-80 bg-white border border-gray-200 rounded-2xl text-gray-400">
                <span className="text-[56px] mb-3 opacity-50">📈</span>
                <p className="my-1 text-[15px]">차트 데이터가 없습니다</p>
              </div>
            )}

            {/* 일별 정보 */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
              <div className="flex justify-between items-center p-4 bg-white border border-gray-100 rounded-xl">
                <span className="text-gray-400 text-[13px]">시가</span>
                <span className="font-semibold text-[13px] text-gray-900">{formatPrice(stock.open)}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-white border border-gray-100 rounded-xl">
                <span className="text-gray-400 text-[13px]">고가</span>
                <span className="font-semibold text-[13px] text-red-500">{formatPrice(stock.high)}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-white border border-gray-100 rounded-xl">
                <span className="text-gray-400 text-[13px]">저가</span>
                <span className="font-semibold text-[13px] text-blue-500">{formatPrice(stock.low)}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-white border border-gray-100 rounded-xl">
                <span className="text-gray-400 text-[13px]">전일종가</span>
                <span className="font-semibold text-[13px] text-gray-900">{formatPrice(stock.prevClose)}</span>
              </div>
            </div>
          </div>
        )}

        {/* 호가 탭 */}
        {activeTab === 'orderbook' && (
          <div className="bg-white border border-gray-100 rounded-2xl p-5">
            {orderbook ? (
              <OrderBook
                data={orderbook}
                currentPrice={stock.price}
                onRefresh={fetchOrderbook}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <span className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4"></span>
                <span>호가 데이터 로딩 중...</span>
              </div>
            )}
            <p className="mt-4 p-3 bg-gray-100 border-l-[3px] border-blue-500 rounded text-[13px] text-gray-500 leading-relaxed">
              * 호가 데이터는 시뮬레이션입니다. 실시간 호가는 한투 API 연동 후 제공됩니다.
            </p>
          </div>
        )}

        {/* 종목정보 탭 */}
        {activeTab === 'info' && (
          <div className="bg-white border border-gray-100 rounded-2xl p-5">
            <div className="grid grid-cols-2 gap-5 md:grid-cols-1">
              <div className="flex flex-col gap-2">
                <span className="text-[13px] text-gray-400">시가총액</span>
                <span className="text-lg font-semibold text-gray-900">{stock.marketCap || '-'}</span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[13px] text-gray-400">PER</span>
                <span className="text-lg font-semibold text-gray-900">{stock.per ? stock.per + '배' : '-'}</span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[13px] text-gray-400">PBR</span>
                <span className="text-lg font-semibold text-gray-900">{stock.pbr ? stock.pbr + '배' : '-'}</span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[13px] text-gray-400">EPS</span>
                <span className="text-lg font-semibold text-gray-900">{stock.eps ? formatNumber(stock.eps) + '원' : '-'}</span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[13px] text-gray-400">배당수익률</span>
                <span className="text-lg font-semibold text-gray-900">{stock.dividend ? stock.dividend + '%' : '-'}</span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[13px] text-gray-400">52주 최고</span>
                <span className="text-lg font-semibold text-red-500">{stock.fiftyTwoWeekHigh ? formatPrice(stock.fiftyTwoWeekHigh) : '-'}</span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[13px] text-gray-400">52주 최저</span>
                <span className="text-lg font-semibold text-blue-500">{stock.fiftyTwoWeekLow ? formatPrice(stock.fiftyTwoWeekLow) : '-'}</span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[13px] text-gray-400">거래량</span>
                <span className="text-lg font-semibold text-gray-900">{formatNumber(stock.volume)}</span>
              </div>
            </div>
          </div>
        )}

        {/* 공시 탭 */}
        {activeTab === 'disclosures' && (
          <div className="p-4">
            <DisclosuresPanel
              disclosures={disclosuresData}
              loading={disclosuresLoading}
            />
            {disclosuresData && disclosuresData.length > 0 && (
              <p className="mt-4 p-3 bg-gray-100 border-l-[3px] border-blue-500 rounded text-[13px] text-gray-500 leading-relaxed">
                * 공시 데이터는 시뮬레이션입니다. 실제 DART API 연동 후 제공됩니다.
              </p>
            )}
          </div>
        )}

        {/* 뉴스 탭 */}
        {activeTab === 'news' && (
          <div className="bg-white border border-gray-100 rounded-2xl px-5 py-10">
            {newsLoading ? (
              <div className="flex flex-col items-center text-gray-400">
                <span className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4"></span>
                <span>뉴스 데이터 로딩 중...</span>
              </div>
            ) : newsData ? (
              <NewsPanel news={newsData} />
            ) : (
              <div className="flex flex-col items-center text-gray-400">
                <span className="text-[56px] mb-3 opacity-50">📰</span>
                <p>뉴스 데이터를 불러올 수 없습니다</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 하단 주문 버튼 */}
      <div className="fixed bottom-0 left-0 right-0 grid grid-cols-2 gap-3 bg-white px-5 py-4 border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] z-[300] md:px-4 md:py-3 md:gap-2">
        <button className="h-[52px] text-lg font-semibold border-none rounded-xl cursor-pointer transition-all duration-150 bg-blue-500 text-white hover:opacity-90 md:h-11 md:text-base">매도</button>
        <button className="h-[52px] text-lg font-semibold border-none rounded-xl cursor-pointer transition-all duration-150 bg-red-500 text-white hover:opacity-90 md:h-11 md:text-base">매수</button>
      </div>
    </div>
  );
};

export default StockDetail;
